import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CadNameTestResult, configurationService } from '../../services/configurationService';

/**
 * Common ways a recorder and a CAD export name their files, as the patterns that pick the part that
 * identifies the call. The part is the group named id. Anything else is "Custom", typed by hand.
 */
const PRESETS: { id: string; label: string; pattern: string }[] = [
  { id: 'default', label: 'Use the default', pattern: '' },
  { id: 'first-word', label: 'The first word (up to a space, underscore or dot)', pattern: String.raw`^(?<id>[^\s_.]+)` },
  { id: 'before-space', label: 'Everything before the first space', pattern: String.raw`^(?<id>\S+)` },
  { id: 'before-underscore', label: 'Everything before the first underscore', pattern: String.raw`^(?<id>[^_]+)` },
  { id: 'number-anywhere', label: 'A number like 2609-04281 anywhere in the name', pattern: String.raw`(?<id>\d{4}-\d{4,})` },
];

const CUSTOM = 'custom';

const presetFor = (pattern: string): string =>
  PRESETS.find((p) => p.pattern === pattern.trim())?.id ?? CUSTOM;

interface SideProps {
  title: string;
  idPrefix: string;
  value: string;
  onChange: (pattern: string) => void;
  sample: string;
  onSample: (sample: string) => void;
  sampleLabel: string;
  samplePlaceholder: string;
  defaultMeaning: string;
}

const Side: React.FC<SideProps> = ({
  title,
  idPrefix,
  value,
  onChange,
  sample,
  onSample,
  sampleLabel,
  samplePlaceholder,
  defaultMeaning,
}) => {
  const selected = presetFor(value);
  return (
    <Stack spacing={1.5} component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
      <Typography component="legend" variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <FormControl sx={{ maxWidth: 520 }}>
        <InputLabel id={`${idPrefix}-preset-label`}>Where the ID is</InputLabel>
        <Select
          labelId={`${idPrefix}-preset-label`}
          label="Where the ID is"
          value={selected}
          onChange={(e) => {
            const preset = PRESETS.find((p) => p.id === e.target.value);
            // "Custom" keeps what is typed; choosing it does not erase the pattern.
            if (preset) onChange(preset.pattern);
          }}
        >
          {PRESETS.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.label}
            </MenuItem>
          ))}
          <MenuItem value={CUSTOM}>Custom pattern</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label="Pattern"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        slotProps={{ htmlInput: { spellCheck: false, style: { fontFamily: 'monospace' } } }}
        helperText={
          value.trim()
            ? 'The part in the group named id (or the first group in parentheses) is the ID.'
            : defaultMeaning
        }
      />
      <TextField
        label={sampleLabel}
        placeholder={samplePlaceholder}
        value={sample}
        onChange={(e) => onSample(e.target.value)}
        helperText="Only for testing. Not saved."
      />
    </Stack>
  );
};

interface CadNameMatchingProps {
  audioPattern: string;
  reportPattern: string;
  onAudioPattern: (pattern: string) => void;
  onReportPattern: (pattern: string) => void;
}

/**
 * Tells the system which part of a recording's file name and which part of a CAD report's file name
 * identify the call, so a report can be matched to its recording when the rest of the two names
 * differs. The tester asks the server, which is what the matching uses, so what it shows is what
 * will happen.
 */
export const CadNameMatching: React.FC<CadNameMatchingProps> = ({
  audioPattern,
  reportPattern,
  onAudioPattern,
  onReportPattern,
}) => {
  const [audioSample, setAudioSample] = useState('');
  const [reportSample, setReportSample] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<CadNameTestResult | null>(null);
  const [failed, setFailed] = useState(false);

  const test = async () => {
    setTesting(true);
    setFailed(false);
    try {
      setResult(
        await configurationService.testCadNames({
          audioSample,
          reportSample,
          audioPattern,
          reportPattern,
        }),
      );
    } catch {
      setResult(null);
      setFailed(true);
    } finally {
      setTesting(false);
    }
  };

  const describe = (): { severity: 'success' | 'warning' | 'error'; text: string } | null => {
    if (failed) return { severity: 'error', text: 'The test could not be run. Try again.' };
    if (!result) return null;
    if (result.audioError || result.reportError) {
      return { severity: 'error', text: [result.audioError, result.reportError].filter(Boolean).join(' ') };
    }
    if (result.matches) {
      return {
        severity: 'success',
        text: `Both names give the ID ${result.audioKey}. A report named like this would be matched to a recording named like that.`,
      };
    }
    const audio = result.audioKey ?? (audioPattern.trim() ? 'nothing' : 'no pattern set, so the call\'s stored incident number is used');
    const report = result.reportKey ?? 'nothing';
    return {
      severity: 'warning',
      text: `They would not be matched. The recording name gives ${audio}. The report name gives ${report}.`,
    };
  };

  const outcome = describe();

  return (
    <Box component="section" aria-labelledby="cad-matching-heading">
      <Typography id="cad-matching-heading" variant="subtitle1" component="h3" gutterBottom>
        Matching reports to recordings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A report is matched to a recording when the part of each file name that identifies the call is
        the same. The rest of the two names can differ, for example <em>2609-04281 Recording.wav</em> and{' '}
        <em>2609-04281 CAD Report.pdf</em>. Say once, for each side, where that part is. Leave a side on the
        default if it already works. Try your own file names below before saving.
      </Typography>
      <Stack spacing={3}>
        <Side
          title="Recording file names"
          idPrefix="cad-audio"
          value={audioPattern}
          onChange={onAudioPattern}
          sample={audioSample}
          onSample={setAudioSample}
          sampleLabel="Example recording file name"
          samplePlaceholder="2609-04281 Recording.wav"
          defaultMeaning="Default: the incident number stored with the call. That is only part of the file name if the Ingestion filename pattern reads it from there."
        />
        <Side
          title="CAD report file names"
          idPrefix="cad-report"
          value={reportPattern}
          onChange={onReportPattern}
          sample={reportSample}
          onSample={setReportSample}
          sampleLabel="Example CAD report file name"
          samplePlaceholder="2609-04281 CAD Report.pdf"
          defaultMeaning="Default: every part of the name that contains a digit is tried as the incident number."
        />
        <Box>
          <Button
            variant="outlined"
            onClick={() => void test()}
            disabled={testing || (!audioSample.trim() && !reportSample.trim())}
            startIcon={testing ? <CircularProgress size={14} aria-hidden /> : undefined}
          >
            Test these names
          </Button>
        </Box>
        <Box role="status" aria-live="polite">
          {outcome && <Alert severity={outcome.severity}>{outcome.text}</Alert>}
        </Box>
      </Stack>
    </Box>
  );
};
